<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter, useStore } from 'dashboard/composables/store';

const POSTPONE_DURATION = 60 * 60 * 1000;
const POSTPONED_UNTIL_KEY = 'yeszap-channel-onboarding-postponed-until';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const { accountId, currentAccount, updateAccount } = useAccount();
const { isAdmin } = useAdmin();

const inboxes = useMapGetter('inboxes/getInboxes');
const isCheckingInboxes = ref(true);
const isCompleting = ref(false);
const postponedUntil = ref(0);
let postponeTimer = null;

const onboardingStatus = computed(
  () => currentAccount.value?.custom_attributes?.yeszap_onboarding_status
);

const hasReadyChannel = computed(() =>
  inboxes.value.some(inbox => {
    const attributes = inbox.additional_attributes || {};
    const isEvolutionChannel = Boolean(attributes.evolution_instance_name);

    return (
      !isEvolutionChannel || attributes.evolution_connection_status === 'open'
    );
  })
);

const isChannelSetupRoute = computed(() =>
  String(route.name || '').startsWith('settings_inbox')
);

const isPostponed = computed(() => postponedUntil.value > Date.now());

const shouldShow = computed(
  () =>
    isAdmin.value &&
    !hasReadyChannel.value &&
    !isCheckingInboxes.value &&
    !isPostponed.value &&
    !isChannelSetupRoute.value
);

const completeOnboarding = async () => {
  if (isCompleting.value || onboardingStatus.value !== 'pending') return;

  isCompleting.value = true;
  try {
    await updateAccount(
      { yeszap_onboarding_status: 'conversations_pending' },
      { silent: true }
    );
  } catch {
    // A temporary persistence error must not block an already configured channel.
  } finally {
    isCompleting.value = false;
  }
};

const postponementStorageKey = () =>
  `${POSTPONED_UNTIL_KEY}-${accountId.value}`;

const clearPostponeTimer = () => {
  if (!postponeTimer) return;

  window.clearTimeout(postponeTimer);
  postponeTimer = null;
};

const scheduleOnboardingReturn = () => {
  clearPostponeTimer();
  const remainingTime = postponedUntil.value - Date.now();

  if (remainingTime <= 0) {
    postponedUntil.value = 0;
    window.localStorage.removeItem(postponementStorageKey());
    return;
  }

  postponeTimer = window.setTimeout(() => {
    postponedUntil.value = 0;
    window.localStorage.removeItem(postponementStorageKey());
    postponeTimer = null;
  }, remainingTime);
};

const loadPostponement = () => {
  postponedUntil.value = Number(
    window.localStorage.getItem(postponementStorageKey()) || 0
  );
  scheduleOnboardingReturn();
};

const postpone = () => {
  postponedUntil.value = Date.now() + POSTPONE_DURATION;
  window.localStorage.setItem(
    postponementStorageKey(),
    String(postponedUntil.value)
  );
  scheduleOnboardingReturn();
};

const connectWhatsapp = () => {
  router.push({
    name: 'settings_inboxes_page_channel',
    params: { accountId: accountId.value, sub_page: 'whatsapp' },
    query: { provider: 'evolution', onboarding: '1' },
  });
};

const chooseAnotherChannel = () => {
  router.push({
    name: 'settings_inbox_new',
    params: { accountId: accountId.value },
    query: { onboarding: '1' },
  });
};

const refreshInboxes = async () => {
  isCheckingInboxes.value = true;
  try {
    await store.dispatch('inboxes/get');
    if (hasReadyChannel.value) await completeOnboarding();
  } finally {
    isCheckingInboxes.value = false;
  }
};

watch(hasReadyChannel, isReady => {
  if (!isReady) return;

  clearPostponeTimer();
  window.localStorage.removeItem(postponementStorageKey());
  completeOnboarding();
});

watch(isChannelSetupRoute, (isSetupRoute, wasSetupRoute) => {
  if (!isSetupRoute && wasSetupRoute) refreshInboxes();
});

onMounted(() => {
  loadPostponement();
  refreshInboxes();
});

onBeforeUnmount(clearPostponeTimer);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="shouldShow"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      :aria-label="t('ACCOUNT_ONBOARDING.TITLE')"
    >
      <section
        class="flex w-full max-w-xl flex-col overflow-hidden rounded-lg border border-n-weak bg-n-solid-2 shadow-2xl"
      >
        <header
          class="flex items-start gap-4 border-b border-n-weak p-5 sm:p-6"
        >
          <div
            class="flex size-11 shrink-0 items-center justify-center rounded-lg bg-n-brand/15"
          >
            <Icon icon="i-woot-whatsapp" class="size-6 text-n-brand" />
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-xs font-medium uppercase text-n-brand">
              {{ $t('ACCOUNT_ONBOARDING.EYEBROW') }}
            </p>
            <h2 class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ $t('ACCOUNT_ONBOARDING.TITLE') }}
            </h2>
            <p class="mt-2 text-sm leading-6 text-n-slate-11">
              {{ $t('ACCOUNT_ONBOARDING.DESCRIPTION') }}
            </p>
          </div>
          <NextButton
            ghost
            slate
            sm
            icon="i-lucide-x"
            :title="$t('ACCOUNT_ONBOARDING.POSTPONE')"
            @click="postpone"
          />
        </header>

        <div class="grid gap-4 p-5 sm:grid-cols-3 sm:p-6">
          <div class="flex gap-3 sm:flex-col">
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-brand text-sm font-semibold text-white"
            >
              <Icon icon="i-lucide-message-square" class="size-4" />
            </div>
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('ACCOUNT_ONBOARDING.STEPS.CHANNEL.TITLE') }}
              </p>
              <p class="mt-1 text-xs leading-5 text-n-slate-11">
                {{ $t('ACCOUNT_ONBOARDING.STEPS.CHANNEL.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="flex gap-3 sm:flex-col">
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-alpha-3 text-sm font-semibold text-n-slate-11"
            >
              <Icon icon="i-lucide-qr-code" class="size-4" />
            </div>
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('ACCOUNT_ONBOARDING.STEPS.QR_CODE.TITLE') }}
              </p>
              <p class="mt-1 text-xs leading-5 text-n-slate-11">
                {{ $t('ACCOUNT_ONBOARDING.STEPS.QR_CODE.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="flex gap-3 sm:flex-col">
            <div
              class="flex size-8 shrink-0 items-center justify-center rounded-full bg-n-alpha-3 text-sm font-semibold text-n-slate-11"
            >
              <Icon icon="i-lucide-check" class="size-4" />
            </div>
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('ACCOUNT_ONBOARDING.STEPS.READY.TITLE') }}
              </p>
              <p class="mt-1 text-xs leading-5 text-n-slate-11">
                {{ $t('ACCOUNT_ONBOARDING.STEPS.READY.DESCRIPTION') }}
              </p>
            </div>
          </div>
        </div>

        <footer
          class="flex flex-col-reverse gap-3 border-t border-n-weak bg-n-alpha-1 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6"
        >
          <NextButton
            link
            slate
            :label="$t('ACCOUNT_ONBOARDING.OTHER_CHANNEL')"
            @click="chooseAnotherChannel"
          />
          <div class="flex flex-col-reverse gap-2 sm:flex-row">
            <NextButton
              outline
              slate
              :label="$t('ACCOUNT_ONBOARDING.POSTPONE')"
              @click="postpone"
            />
            <NextButton
              solid
              blue
              icon="i-lucide-qr-code"
              :label="$t('ACCOUNT_ONBOARDING.CONNECT_WHATSAPP')"
              @click="connectWhatsapp"
            />
          </div>
        </footer>
      </section>
    </div>
  </Teleport>
</template>
